import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Shield, Check, Loader2, AlertCircle, ChevronDown } from "lucide-react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * NDASignModal — reusable NDA signing modal.
 *
 * Props:
 *  open          — boolean
 *  onOpenChange  — (bool) => void
 *  onSignComplete — () => void  called after successful sign
 *  pdfUrl        — presigned S3 URL to render (if null, fetches from /settings)
 *  s3Key         — S3 key to save with the signature (optional, for deal NDA)
 *  requireNameMatch — if true, user must type their exact account name to sign
 *  mode          — "platform" | "deal"  (default "platform")
 *  onDecline     — optional, called when user declines (platform mode logs out)
 */
const NDASignModal = ({
  open,
  onOpenChange,
  onSignComplete,
  pdfUrl: pdfUrlProp = null,
  s3Key = null,
  requireNameMatch = false,
  mode = "platform",
  onDecline,
}) => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [numPages, setNumPages] = useState(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [nameError, setNameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(800);

  const scrollRef = useRef(null);
  const containerRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (open) {
      setReachedEnd(false);
      setAgreed(false);
      setLegalName("");
      setNameError("");
      if (pdfUrlProp) {
        setPdfUrl(pdfUrlProp);
      } else {
        fetchPdfUrl();
      }
    }
  }, [open, pdfUrlProp]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width || 800);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [pdfUrl]);

  const fetchPdfUrl = async () => {
    setError(null);
    setPdfUrl(null);
    try {
      const response = await axios.get(`${API}/settings`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      const url = mode === "deal" ? response.data?.deal_nda : response.data?.nda;
      if (url) {
        setPdfUrl(url);
      } else {
        setError("NDA document not found. Please contact administrator.");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError("NDA document not found. Please contact administrator.");
      } else {
        setError("Failed to load NDA document. Please try again.");
      }
    }
  };

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || reachedEnd) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setReachedEnd(true);
  }, [reachedEnd]);

  const validateName = (value) => {
    if (!requireNameMatch) return true;
    if (value !== user?.name) {
      setNameError(`Name must exactly match your account name: "${user?.name}"`);
      return false;
    }
    setNameError("");
    return true;
  };

  const handleSign = async () => {
    if (!agreed || !legalName.trim()) return;
    if (!validateName(legalName.trim())) return;

    setLoading(true);
    try {
      if (mode === "platform") {
        await axios.put(
          `${API}/auth/profile`,
          { nda_signed: { signed: new Date().toISOString(), legal_name: legalName.trim() } },
          { withCredentials: true, headers: getAuthHeaders() }
        );
        toast.success("NDA signed successfully!");
        await checkAuth();
      }
      // For deal mode, the caller handles the API call via onSignComplete
      if (onSignComplete) onSignComplete({ legalName: legalName.trim(), s3Key });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign NDA. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (onDecline) {
      onDecline();
    } else if (mode === "platform") {
      await logout();
      navigate("/login", { replace: true });
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={mode === "deal" ? onOpenChange : () => {}}>
      <DialogContent
        showCloseButton={mode === "deal"}
        className="sm:max-w-[860px] max-h-[95vh] flex flex-col gap-0 p-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <DialogTitle className="text-lg font-heading">
                {mode === "deal" ? "Deal Non-Disclosure Agreement" : "Platform Non-Disclosure Agreement"}
              </DialogTitle>
              <DialogDescription className="text-sm">
                {reachedEnd
                  ? "Enter your full name and accept to continue"
                  : "Scroll to the bottom to accept or decline"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* PDF scroll area */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto bg-muted/30 min-h-0"
        >
          {error ? (
            <div className="flex items-start gap-3 m-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          ) : pdfUrl ? (
            <div ref={containerRef} className="flex flex-col items-center py-4 px-2 gap-2">
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={
                  <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
                    Loading document...
                  </div>
                }
              >
                {Array.from({ length: numPages || 0 }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={Math.min(containerWidth - 16, 820)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="shadow-md mb-2"
                  />
                ))}
              </Document>
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
              Loading document...
            </div>
          )}

          {pdfUrl && !reachedEnd && (
            <div className="sticky bottom-0 flex justify-center pb-3 pointer-events-none">
              <span className="flex items-center gap-1 text-xs text-muted-foreground bg-background/80 backdrop-blur px-3 py-1.5 rounded-full border shadow-sm">
                <ChevronDown className="w-3.5 h-3.5 animate-bounce" />
                Scroll to the bottom to continue
              </span>
            </div>
          )}
        </div>

        {/* Sign form */}
        {reachedEnd && (
          <div className="px-6 py-4 border-t bg-card shrink-0 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="legal-name" className="text-sm font-medium">
                {requireNameMatch
                  ? <>Full Name <span className="text-red-500">*</span> <span className="text-xs text-muted-foreground font-normal">(must match your account name exactly)</span></>
                  : <>Legal Name <span className="text-red-500">*</span></>
                }
              </Label>
              <Input
                id="legal-name"
                placeholder={requireNameMatch ? user?.name : "Enter your full legal name"}
                value={legalName}
                onChange={(e) => {
                  setLegalName(e.target.value);
                  if (nameError) validateName(e.target.value);
                }}
                onBlur={(e) => validateName(e.target.value)}
                disabled={loading}
                className={nameError ? "border-red-500" : ""}
              />
              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
              {!nameError && requireNameMatch && legalName && legalName === user?.name && (
                <p className="text-xs text-green-600">✓ Name matches</p>
              )}
            </div>

            <div className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30">
              <Checkbox
                id="nda-agreement"
                checked={agreed}
                onCheckedChange={setAgreed}
                disabled={loading}
                className="mt-0.5"
              />
              <Label htmlFor="nda-agreement" className="text-sm leading-relaxed cursor-pointer">
                I have read and agree to be bound by all terms of this Non-Disclosure Agreement.
                My electronic signature has the same legal effect as a handwritten signature.
              </Label>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 pt-0">
              <Button
                variant="outline"
                onClick={handleDecline}
                disabled={loading}
                className="flex-1 text-destructive border-destructive/40 hover:bg-destructive/10"
              >
                Decline & {mode === "platform" ? "Logout" : "Cancel"}
              </Button>
              <Button
                onClick={handleSign}
                disabled={
                  !agreed ||
                  !legalName.trim() ||
                  loading ||
                  (requireNameMatch && legalName.trim() !== user?.name)
                }
                className="flex-1 bg-green-500 hover:bg-green-600"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />I Agree & Sign NDA</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NDASignModal;
